import { LanguageContext } from '@/i18n/LanguageContext';
import { Component, type ContextType, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional per-boundary label, e.g. "Dashboard" - shown in the fallback UI. */
  boundaryName?: string;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time/runtime exceptions thrown by a subtree (as opposed to
 * TanStack Query's data-fetch errors, which each page handles inline via
 * <InlineError>). Wrap every top-level page in one so a bug in a single view
 * can't take down the whole SPA (docs/AGENT_PROMPT.md §6: "error boundaries
 * ... on every data view").
 */
export class ErrorBoundary extends Component<Props, State> {
  static contextType = LanguageContext;
  declare context: ContextType<typeof LanguageContext>;
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error(
      `[ErrorBoundary${this.props.boundaryName ? `:${this.props.boundaryName}` : ''}]`,
      error,
      info,
    );
  }

  private reset = (): void => {
    this.setState({ error: null });
  };

  render() {
    const { errorText, t } = this.context;
    if (this.state.error) {
      return (
        <div className="error-state glass-panel" role="alert">
          <h3>
            {t('This section failed to render.')}
            {this.props.boundaryName && ` (${this.props.boundaryName})`}
          </h3>
          <p>{errorText(this.state.error.message)}</p>
          <button type="button" className="btn btn--secondary" onClick={this.reset}>
            {t('Try again')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
